import React from "react";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Stack,
  LinearProgress,
} from "@mui/material";

import { useTranslation } from "react-i18next";
import Adjust from "@mui/icons-material/Adjust";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Park from "@mui/icons-material/Park";
import { CalibrationItem, Coverage } from "../Calibration.types";

type Props = {
  calibrationItemCount: number;
  calibrationSet: CalibrationItem[];
  coverage: Coverage;
  onSelect: (sample: CalibrationItem) => void;
  onEdit: (plotId: number) => void;
  onDelete: (plotId: number) => void;
  activePlotId: number | null;
};

export function CalibrationCoverage({
  calibrationItemCount: sampleCount,
  calibrationSet: calibrationSamples,
  coverage,
  onSelect,
  onEdit,
  onDelete,
  activePlotId,
}: Props) {
  const { t } = useTranslation();

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={1.5}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("header.calibrate.distribution")}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          mb={3}
          sx={{ lineHeight: 1.6 }}
        >
          {t("text.calibrate.samples_explanation")}{" "}
          <Box component="strong" sx={{ color: "text.primary" }}>
            {t("text.calibrate.select_trees")}
          </Box>
        </Typography>

        <Stack spacing={3}>
          {Object.keys(coverage).map((key) => {
            const item = coverage[key];
            return (
              <ProgressItem
                key={key}
                count={item.count}
                minCount={item.minCount}
                color={item.color}
                bucketIndex={item.bucketIndex}
              />
            );
          })}
        </Stack>
      </Box>
      {sampleCount === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            px: 2,
            bgcolor: "var(--boxed-background)",
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "divider",
            mt: 2,
          }}
        >
          <Adjust sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {t("text.calibrate.no_samples")}
          </Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ mt: 2, borderRadius: 2 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "var(--boxed-background)" }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: 220,
                    textAlign: "left",
                    paddingLeft: "32px",
                  }}
                >
                  {t("text.calibrate.tree")}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, width: 150, textAlign: "center" }}
                >
                  {t("table.calibrate.column_header.manual_count")}
                </TableCell>
                <TableCell sx={{ width: 60 }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {calibrationSamples.map((sample, index) => {
                const isActive = sample.plotId === activePlotId;

                return (
                  <TableRow
                    key={sample.plotId}
                    hover
                    onClick={() => onSelect(sample)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: isActive ? "action.selected" : undefined,
                      "&:last-child td": { border: 0 },
                    }}
                  >
                    <TableCell align="center" sx={{ p: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Park
                          htmlColor={
                            sample.yieldCategory === 2
                              ? "var(--color-apple-calibration-map-2-alternative)"
                              : `var(${sample.color})`
                          }
                          fontSize="inherit"
                        />
                        <Stack direction="column">
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            textAlign="left"
                          >
                            <span>
                              {t("text.calibrate.sample")} {index + 1}
                            </span>
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            textAlign="left"
                          >
                            <span>
                              {t("text.calibrate.tree")} {sample.plotId}
                            </span>
                          </Typography>
                        </Stack>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Typography
                        fontWeight={600}
                        color="success.main"
                        textAlign="center"
                      >
                        {sample.manualCount}
                      </Typography>
                    </TableCell>

                    <TableCell align="right" sx={{ p: 0.5 }}>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                        pr={1}
                      >
                        <IconButton
                          size="small"
                          color="default"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(sample.plotId);
                          }}
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(sample.plotId);
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

function ProgressItem({
  count,
  minCount,
  color,
  bucketIndex,
}: {
  count: number;
  minCount: number;
  color: string;
  bucketIndex: number;
}) {
  const { t } = useTranslation();
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={0.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "3px",
              bgcolor: `var(${color})`,
              boxShadow: 1,
            }}
          />
          <Typography variant="body2" fontWeight="bold" color="success">
            {t(`label.calibrate.yield.category-${bucketIndex}`)}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          fontWeight="bold"
          color={count >= minCount ? "success" : "text.secondary"}
        >
          {`${count} / ${minCount}`}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min((count / minCount) * 100, 100)}
        color="success"
        sx={{ height: 8, borderRadius: 4, bgcolor: "rgba(0,0,0,0.05)" }}
      />
    </Box>
  );
}
